import { useState } from 'react';
import { AlertTriangle } from 'lucide-react';

interface ConfirmDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'warning' | 'info';
  /** If provided, shows a textarea input. The value is passed to onConfirm. */
  textInput?: {
    placeholder: string;
    minLength?: number;
    label?: string;
  };
  onConfirm: (text?: string) => void;
  onCancel: () => void;
}

export function ConfirmDialog({
  isOpen,
  title,
  message,
  confirmLabel = 'Confirmar',
  cancelLabel = 'Cancelar',
  variant = 'danger',
  textInput,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const [text, setText] = useState('');

  if (!isOpen) return null;

  const minLen = textInput?.minLength || 0;
  const isTextValid = !textInput || text.trim().length >= minLen;

  const variantStyles = {
    danger: { icon: 'bg-red-100 text-red-600', button: 'bg-red-600 hover:bg-red-700' },
    warning: { icon: 'bg-amber-100 text-amber-600', button: 'bg-amber-600 hover:bg-amber-700' },
    info: { icon: 'bg-blue-100 text-blue-600', button: 'bg-blue-600 hover:bg-blue-700' },
  };
  const styles = variantStyles[variant];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[60]">
      <div className="bg-white rounded-xl max-w-sm w-full shadow-xl">
        <div className="p-6">
          <div className="flex items-start space-x-3 mb-4">
            <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${styles.icon}`}>
              <AlertTriangle className="w-4 h-4" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-gray-900">{title}</h3>
              <p className="text-sm text-gray-600 mt-1">{message}</p>
            </div>
          </div>

          {textInput && (
            <div className="mt-4">
              {textInput.label && (
                <label className="block text-sm font-medium text-gray-700 mb-1">{textInput.label}</label>
              )}
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder={textInput.placeholder}
                rows={3}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
              />
              {minLen > 0 && (
                <p className={`text-xs mt-1 ${text.trim().length >= minLen ? 'text-gray-400' : 'text-red-500'}`}>
                  Mínimo {minLen} caracteres ({text.trim().length}/{minLen})
                </p>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center gap-3 px-6 py-4 border-t border-gray-100">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={() => onConfirm(textInput ? text.trim() : undefined)}
            disabled={!isTextValid}
            className={`flex-1 px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${styles.button}`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
